import React from 'react';
import {
  Row, Col, Button, Form, Card,
} from 'react-bootstrap';
import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BurnBedsGrid, { getBurnBedsStaffingValidation } from 'components/forms/BurnBedsGrid';
import RehabBedsGrid from 'components/forms/RehabBedsGrid';
import api from 'api';
import useUser from 'hooks/useUser';
import useFeedback from 'hooks/useFeedback';
import { BenchmarkType } from 'types/burnCenter';

type BenchmarkValues = BenchmarkType;

type Props = {
  id: string;
  canEdit: boolean;
  values: Readonly<Partial<BenchmarkValues>>;
  onSaved?: () => void;
};

const emptyToNull = (v: unknown) => {
  if (typeof v === 'string') {
    if (v.trim() === '') {
      return null;
    }
    return v.trim();
  }
  return v;
};

const toIntOrNull = (v: unknown) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const intField = () => yup.number().transform(toIntOrNull).nullable().defined();

const schema: yup.ObjectSchema<BenchmarkValues> = yup
  .object({
    averageDailyHospitalOccupancy: intField(),
    hospitalTotalStaffedBeds: intField(),
    hospitalTotalStaffedIcuBeds: intField(),
    burnLicensedAdultBeds: intField(),
    burnLicensedPediatricBeds: intField(),
    burnLicensedTotalBeds: intField(),
    burnStaffedIcuAdultBeds: intField(),
    burnStaffedIcuPediatricBeds: intField(),
    burnStaffedIcuTotalBeds: intField(),
    burnStaffedNonIcuAdultBeds: intField(),
    burnStaffedNonIcuPediatricBeds: intField(),
    burnStaffedNonIcuTotalBeds: intField(),
    burnStaffedTotalAdultBeds: intField(),
    burnStaffedTotalPediatricBeds: intField(),
    burnStaffedTotalBeds: intField(),
    rehabLicensedInpatientAdultBeds: intField(),
    rehabLicensedInpatientPediatricBeds: intField(),
    rehabLicensedInpatientTotalBeds: intField(),
    pediatricPatientDefinition: yup
      .string()
      .transform(emptyToNull)
      .nullable()
      .defined(),
  })
  .test('burn-beds-staffing', function validateBurnBedsStaffing(values) {
    const result = getBurnBedsStaffingValidation(values);
    if (!result.isValid) {
      return this.createError({
        message: result.messages.join(' '),
      });
    }
    return true;
  })
  .required();

const normalizeDefaults = (v: Partial<BenchmarkValues>): BenchmarkValues => ({
  averageDailyHospitalOccupancy: v.averageDailyHospitalOccupancy ?? null,
  hospitalTotalStaffedBeds: v.hospitalTotalStaffedBeds ?? null,
  hospitalTotalStaffedIcuBeds: v.hospitalTotalStaffedIcuBeds ?? null,
  burnLicensedAdultBeds: v.burnLicensedAdultBeds ?? null,
  burnLicensedPediatricBeds: v.burnLicensedPediatricBeds ?? null,
  burnLicensedTotalBeds: v.burnLicensedTotalBeds ?? null,
  burnStaffedIcuAdultBeds: v.burnStaffedIcuAdultBeds ?? null,
  burnStaffedIcuPediatricBeds: v.burnStaffedIcuPediatricBeds ?? null,
  burnStaffedIcuTotalBeds: v.burnStaffedIcuTotalBeds ?? null,
  burnStaffedNonIcuAdultBeds: v.burnStaffedNonIcuAdultBeds ?? null,
  burnStaffedNonIcuPediatricBeds: v.burnStaffedNonIcuPediatricBeds ?? null,
  burnStaffedNonIcuTotalBeds: v.burnStaffedNonIcuTotalBeds ?? null,
  burnStaffedTotalAdultBeds: v.burnStaffedTotalAdultBeds ?? null,
  burnStaffedTotalPediatricBeds: v.burnStaffedTotalPediatricBeds ?? null,
  burnStaffedTotalBeds: v.burnStaffedTotalBeds ?? null,
  rehabLicensedInpatientAdultBeds: v.rehabLicensedInpatientAdultBeds ?? null,
  rehabLicensedInpatientPediatricBeds: v.rehabLicensedInpatientPediatricBeds ?? null,
  rehabLicensedInpatientTotalBeds: v.rehabLicensedInpatientTotalBeds ?? null,
  pediatricPatientDefinition: v.pediatricPatientDefinition ?? null,
});

const ReadOnly: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="mb-3">
    <h6 className="fw-bold mb-1" style={{ minHeight: '2.5rem' }}>
      {label}
    </h6>
    <p className="mb-0">{value ?? ''}</p>
  </div>
);

type NumberFieldProps = {
  label: string;
  name: keyof BenchmarkValues;
  form: UseFormReturn<BenchmarkValues>;
  editing: boolean;
  values: BenchmarkValues;
  disabled?: boolean;
  suffix?: string;
  min?: number;
  max?: number;
};

const NumberField: React.FC<NumberFieldProps> = ({
  label,
  name,
  form,
  editing,
  values,
  disabled,
  suffix,
  min,
  max,
}) => {
  const err = form.formState.errors[name]?.message as string | undefined;
  if (!editing) {
    const val = values[name] as number | null;
    return (
      <ReadOnly
        label={label}
        value={val != null ? `${val}${suffix ?? ''}` : <span className="text-muted fst-italic">n/a</span>}
      />
    );
  }
  return (
    <Form.Group>
      <Form.Label className="fw-bold">
        {label}
        {suffix ? ` (${suffix})` : ''}
      </Form.Label>
      <Form.Control
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        {...form.register(name)}
        isInvalid={!!err}
        disabled={disabled}
      />
      <Form.Control.Feedback type="invalid">{err}</Form.Control.Feedback>
    </Form.Group>
  );
};

const BenchmarkTab: React.FC<Props> = ({
  id,
  canEdit,
  values: incoming,
  onSaved,
}) => {
  const { isAdmin, isBdataAmin } = useUser();
  const [editing, setEditing] = React.useState(false);
  const [burnBedsStaffingValid, setBurnBedsStaffingValid] = React.useState(true);
  const { setError } = useFeedback?.() ?? {
    setError: () => {},
  };
  const defaults = React.useMemo(() => normalizeDefaults(incoming), [incoming]);

  const form = useForm<BenchmarkValues>({
    defaultValues: defaults,
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { isSubmitting, isDirty } = form.formState;

  React.useEffect(() => {
    if (!editing) form.reset(normalizeDefaults(incoming));
  }, [incoming, editing, form]);

  const onSubmit = React.useCallback(
    async (data: BenchmarkValues) => {
      try {
        if (isAdmin || isBdataAmin) {
          await api.updateBurnCenterBenchmarkById(id, data);
        } else {
          await api.updateCurrentBurnCenterBenchmark(data);
        }
        setEditing(false);
        onSaved?.();
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        setEditing(false);
        setError?.(detail || 'Failed to update benchmark data.');
      }
    },
    [id, isAdmin, isBdataAmin, onSaved, setError],
  );

  const handleCancel = React.useCallback(() => {
    form.reset(defaults);
    setEditing(false);
  }, [defaults, form]);

  const fieldColProps = editing
    ? { xs: 12, sm: 6, lg: 6 }
    : { xs: 12, sm: 6, lg: 6 };

  return (
    <div>
      <div className="d-flex justify-content-end align-items-end mb-3">
        <div className="flex-1 align-content-center" style={{ flexGrow: 1 }}>
          <a href="/training" style={{ fontSize: '0.8rem' }} target="_blank">
            Bed Capacity Field Definitions
          </a>
        </div>
        {canEdit && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {canEdit && editing && (
          <div>
            <Button
              variant="secondary"
              className="me-2"
              type="submit"
              form="benchmark-form"
              disabled={isSubmitting || !isDirty || !burnBedsStaffingValid}
            >
              {isSubmitting ? 'Updating…' : 'Update'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <Form
        id="benchmark-form"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
      >
        <Card className="mt-4 border shadow-sm">
          <Card.Body>
            <h5 className="fw-bold mb-4">Hospital Beds</h5>
            <Row className="g-3 align-items-start">
              <Col {...fieldColProps}>
                <NumberField
                  label="Hospital Total Beds (staffed)"
                  name="hospitalTotalStaffedBeds"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                />
              </Col>
              <Col {...fieldColProps}>
                <NumberField
                  label="Hospital Total ICU Beds (staffed)"
                  name="hospitalTotalStaffedIcuBeds"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                />
              </Col>
              <Col {...fieldColProps}>
                <NumberField
                  label="Average Daily Hospital Occupancy (%)"
                  name="averageDailyHospitalOccupancy"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  min={0}
                  max={100}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card className="mt-4 border shadow-sm">
          <Card.Body>
            <BurnBedsGrid
              form={form}
              namePrefix=""
              editing={editing}
              disabled={isSubmitting}
              readOnlyValues={defaults}
              onStaffingValidationChange={setBurnBedsStaffingValid}
            />
          </Card.Body>
        </Card>
        <Card className="mt-4 border shadow-sm">
          <Card.Body>
            <RehabBedsGrid
              form={form}
              namePrefix=""
              editing={editing}
              disabled={isSubmitting}
              readOnlyValues={defaults}
            />
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default BenchmarkTab;
